import React, { useMemo, useState } from 'react';
import { ActivityIndicator, ScrollView, TextInput, View } from 'react-native';
import { Switch } from '@/components/ui/switch';
import { Ionicons } from '@expo/vector-icons';

import { ThemedText } from '@/components/themed-text';
import { PressableScale } from '@/components/ui/pressable-scale';
import { useBlocks, useCreateBlock, useDeleteBlock } from '@/hooks/use-appointments';
import { getApiErrorMessage } from '@/utils/api-error';
import { useToastStore } from '@/store/toastStore';
import {
  dayParts,
  formatAppointmentWhen,
  formatSlotRange,
  zonedDateKey,
  zonedLocalToIso,
} from '@/utils/format-appointment';
import { TimeField } from './time-field';
import { useThemedHex } from '@/hooks/use-theme-colors';

function nextDayKeys(count: number): string[] {
  const today = new Date();
  return Array.from({ length: count }, (_, i) => {
    const d = new Date(today.getTime() + i * 86400000);
    return zonedDateKey(d.toISOString());
  });
}

/** Bloqueios (A4): lista, cria (horas ou dia inteiro, com motivo) e remove. */
export function BlockList() {
  const th = useThemedHex();
  const { data: blocks, isLoading } = useBlocks();
  const createBlock = useCreateBlock();
  const deleteBlock = useDeleteBlock();
  const { addToast } = useToastStore();

  const days = useMemo(() => nextDayKeys(30), []);
  const [date, setDate] = useState(days[0]);
  const [allDay, setAllDay] = useState(false);
  const [startTime, setStartTime] = useState('09:00');
  const [endTime, setEndTime] = useState('12:00');
  const [reason, setReason] = useState('');

  const sorted = useMemo(
    () =>
      [...(blocks ?? [])].sort(
        (a, b) => new Date(a.startAt).getTime() - new Date(b.startAt).getTime(),
      ),
    [blocks],
  );

  const handleCreate = () => {
    const startAt = zonedLocalToIso(date, allDay ? '00:00' : startTime);
    const endAt = zonedLocalToIso(date, allDay ? '23:59' : endTime);
    if (new Date(endAt).getTime() <= new Date(startAt).getTime()) {
      addToast('O fim deve ser após o início.', 'error');
      return;
    }
    createBlock.mutate(
      { startAt, endAt, allDay, reason: reason.trim() || undefined },
      {
        onSuccess: () => {
          addToast('Bloqueio criado.', 'success');
          setReason('');
        },
        onError: (err: any) =>
          addToast(getApiErrorMessage(err, 'Não foi possível criar.'), 'error'),
      },
    );
  };

  return (
    <View className="gap-4">
      {/* Criar */}
      <View
        className="gap-3 rounded-2xl border border-neutral-100 bg-card p-4"
        style={{ borderCurve: 'continuous' }}
      >
        <ThemedText className="font-bold text-sm text-neutral-900">Novo bloqueio</ThemedText>

        <ScrollView
          keyboardShouldPersistTaps="handled"
          horizontal
          showsHorizontalScrollIndicator={false}
          contentContainerStyle={{ gap: 8, paddingVertical: 2 }}
        >
          {days.map((key) => {
            const active = key === date;
            const parts = dayParts(key);
            return (
              <PressableScale
                key={key}
                onPress={() => setDate(key)}
                className="items-center rounded-2xl px-3 py-2"
                style={{
                  minWidth: 54,
                  backgroundColor: active ? '#FD5001' : th('#F5F5F5'),
                  borderCurve: 'continuous',
                }}
              >
                <ThemedText
                  className="font-medium text-[11px]"
                  style={{ color: active ? 'rgba(255,255,255,0.85)' : th('#737373') }}
                >
                  {parts.weekday}
                </ThemedText>
                <ThemedText
                  className="font-bold text-base"
                  style={{ color: active ? '#fff' : th('#171717') }}
                >
                  {parts.day}
                </ThemedText>
              </PressableScale>
            );
          })}
        </ScrollView>

        <View className="flex-row items-center justify-between">
          <ThemedText className="font-medium text-sm text-neutral-700">Dia inteiro</ThemedText>
          <Switch checked={allDay} onCheckedChange={setAllDay} />
        </View>

        {!allDay && (
          <View className="flex-row items-center gap-2">
            <TimeField value={startTime} label="Início" onChange={setStartTime} />
            <ThemedText className="text-neutral-400">até</ThemedText>
            <TimeField value={endTime} label="Fim" onChange={setEndTime} />
          </View>
        )}

        <TextInput
          value={reason}
          onChangeText={setReason}
          placeholder="Motivo (opcional)"
          placeholderTextColor="#a3a3a3"
          className="rounded-xl border border-neutral-200 bg-card px-3.5 py-2.5 text-sm text-neutral-900"
          style={{ borderCurve: 'continuous' }}
        />

        <PressableScale
          haptic
          disabled={createBlock.isPending}
          onPress={handleCreate}
          className="flex-row items-center justify-center gap-2 rounded-full bg-primary py-3"
        >
          {createBlock.isPending ? (
            <ActivityIndicator color="#fff" size="small" />
          ) : (
            <>
              <Ionicons name="add-circle-outline" size={18} color="#fff" />
              <ThemedText className="font-bold text-sm text-white">Bloquear período</ThemedText>
            </>
          )}
        </PressableScale>
      </View>

      {/* Lista */}
      {isLoading ? (
        <View className="items-center py-6">
          <ActivityIndicator color="#FD5001" />
        </View>
      ) : sorted.length === 0 ? (
        <ThemedText className="px-1 text-sm text-neutral-500">Nenhum bloqueio ativo.</ThemedText>
      ) : (
        <View className="gap-2">
          {sorted.map((block) => (
            <View
              key={block.id}
              className="flex-row items-center gap-3 rounded-2xl border border-neutral-100 bg-card p-3.5"
              style={{ borderCurve: 'continuous' }}
            >
              <View className="h-9 w-9 items-center justify-center rounded-full bg-red-50 dark:bg-error-500">
                <Ionicons
                  className="text-red-700 dark:text-white"
                  name="lock-closed-outline"
                  size={16}
                />
              </View>
              <View className="min-w-0 flex-1">
                <ThemedText className="font-semibold text-sm text-neutral-900">
                  {block.allDay
                    ? `${formatAppointmentWhen(block.startAt).split(' • ')[0]} · dia inteiro`
                    : formatAppointmentWhen(block.startAt)}
                </ThemedText>
                {!block.endAt ? (
                  <ThemedText className="text-xs text-neutral-500">Sem data para voltar</ThemedText>
                ) : !block.allDay ? (
                  <ThemedText className="text-xs text-neutral-500">
                    {formatSlotRange(block.startAt, block.endAt)}
                  </ThemedText>
                ) : null}
                {!!block.reason && (
                  <ThemedText className="text-xs text-neutral-400" numberOfLines={1}>
                    {block.reason}
                  </ThemedText>
                )}
              </View>
              <PressableScale
                haptic
                disabled={deleteBlock.isPending}
                onPress={() => deleteBlock.mutate(block.id)}
                hitSlop={8}
                accessibilityLabel="Remover bloqueio"
              >
                <Ionicons className="text-red-700" name="trash-outline" size={18} />
              </PressableScale>
            </View>
          ))}
        </View>
      )}
    </View>
  );
}
